import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, LockOpen, Minus, RotateCcw, Snowflake } from 'lucide-react';
import * as React from 'react';
import {
  ASSESSMENT_STAGES,
  chainIncludes,
  currentPeriod,
  fiscalYearLabel,
  formatPlainDate,
  GRADES,
  validateTargets,
  STAGE_LABELS,
  type Grade,
  type GradeTarget,
  type ReviewChain,
  type WindowOverride,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  toast,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PlainDatePicker } from '@/components/plain-date-picker';
import { PILL_CLASS, windowReasonVariant, WINDOW_REASON_LABELS } from '@/lib/badges';
import { api, type ApiError } from '@/lib/api';
import { useRatingTargets, useSetRatingTargets } from '@/lib/calibration-api';
import { useCycles, type Cycle, type Quarter } from '@/lib/goals-api';
import { cn } from '@/lib/utils';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

/**
 * Every cycle mutation returns the whole cycle, so one hook covers all four and
 * the cache is replaced from the response rather than re-derived here.
 */
function useCycleMutation<TBody>(request: (body: TBody) => Promise<Cycle>) {
  const queryClient = useQueryClient();
  return useMutation<Cycle, ApiError, TBody>({
    mutationFn: request,
    onSuccess: (next) => {
      queryClient.setQueryData(['cycles'], (prev: Cycle[] | undefined) =>
        prev?.some((c) => c.id === next.id)
          ? prev.map((c) => (c.id === next.id ? next : c))
          : [next, ...(prev ?? [])].sort((a, b) => b.fyStartYear - a.fyStartYear),
      );
      void queryClient.invalidateQueries({ queryKey: ['cycle', 'current'] });
      // Every assessment's canEditSelf depends on the window and the freeze.
      void queryClient.invalidateQueries({ queryKey: ['assessment'] });
    },
  });
}

/**
 * The quarter's review chain, as a board of the stages it will run.
 *
 * Self, Manager and the employee's acceptance are shown but not switchable: they are
 * the assessment itself, not a review policy. HR and Management are the two a quarter
 * can legitimately end at, and the choice is made here, when the quarter is opened.
 *
 * It locks the moment anybody submits into the quarter - dropping HR mid-cycle would
 * strand every assessment already sitting in HR's queue, and adding it would put a
 * reviewer on appraisals that were already signed. The card says so rather than
 * offering a control the API refuses.
 */
function ReviewChainRow({ quarter }: { quarter: Quarter }) {
  const save = useCycleMutation<ReviewChain>((body) =>
    api.patch<Cycle>(`/quarters/${quarter.id}/chain`, body),
  );
  const [chain, setChain] = React.useState<ReviewChain>(quarter.chain);

  // Snap back to the server's answer whenever it moves - a failed save must not leave
  // the board showing a flow the quarter is not running.
  React.useEffect(() => setChain(quarter.chain), [quarter.chain.hr, quarter.chain.management]);

  const editable = quarter.chainEditable;
  const dirty = chain.hr !== quarter.chain.hr || chain.management !== quarter.chain.management;
  // The API refuses this too. Greying the button says why before the click does.
  const empty = !chain.hr && !chain.management;

  return (
    <div className="space-y-2 rounded-lg border border-dashed p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-xs">Review flow</Label>
        {!editable && (
          <span className="text-xs text-muted-foreground">
            {quarter.frozen
              ? 'Frozen — settled for this quarter.'
              : 'Assessments are already in review — settled for this quarter.'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {ASSESSMENT_STAGES.map((stage, i) => {
          const optional = stage === 'HR' || stage === 'MANAGEMENT';
          const on = chainIncludes(stage, chain);
          return (
            <React.Fragment key={stage}>
              {i > 0 && <span className="text-xs text-muted-foreground">→</span>}
              <Button hint={"Include or skip this stage in the review flow"}
                type="button"
                size="sm"
                variant={on ? 'secondary' : 'outline'}
                // A fixed stage is not a control: it renders as a chip and takes no click.
                disabled={!optional || !editable || save.isPending}
                className={cn('h-7 gap-1.5 px-2 text-xs', !on && 'text-muted-foreground')}
                onClick={() =>
                  setChain((c) => ({ ...c, [stage === 'HR' ? 'hr' : 'management']: !on }))
                }
              >
                {optional &&
                  (on ? <Check className="size-3" /> : <Minus className="size-3" />)}
                {STAGE_LABELS[stage]}
              </Button>
            </React.Fragment>
          );
        })}
      </div>

      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <Button hint={"Save the review flow"}
            size="sm"
            variant="outline"
            disabled={!dirty || empty || save.isPending}
            onClick={() => {
              void save
                .mutateAsync(chain)
                .then(() => toast.success(`${quarter.label} review flow saved`))
                .catch((x: unknown) => {
                  setChain(quarter.chain);
                  toast.error('Could not change the flow', { description: err(x) });
                });
            }}
          >
            Save flow
          </Button>
          <span className="text-xs text-muted-foreground">
            {empty
              ? 'A quarter needs HR or Management to close it — one of them records the final rating.'
              : 'Set this before anyone submits. It locks on the first submission.'}
          </span>
        </div>
      )}
    </div>
  );
}

function QuarterRow({ quarter, isCurrent }: { quarter: Quarter; isCurrent: boolean }) {
  const setWindow = useCycleMutation<{ forcedState: WindowOverride; reason?: string }>((body) =>
    api.patch<Cycle>(`/quarters/${quarter.id}/window`, body),
  );
  const setDates = useCycleMutation<{ opensOn: string; closesOn: string }>((body) =>
    api.patch<Cycle>(`/quarters/${quarter.id}`, body),
  );
  const setFrozen = useCycleMutation<{ frozen: boolean }>((body) =>
    api.post<Cycle>(`/quarters/${quarter.id}/freeze`, body),
  );

  const [reason, setReason] = React.useState('');
  const [opensOn, setOpensOn] = React.useState(quarter.window.opensOn);
  const [closesOn, setClosesOn] = React.useState(quarter.window.closesOn);

  // The server owns these dates; if it rejects an edit the inputs must snap back
  // rather than keep showing a window that was never saved.
  React.useEffect(() => {
    setOpensOn(quarter.window.opensOn);
    setClosesOn(quarter.window.closesOn);
  }, [quarter.window.opensOn, quarter.window.closesOn]);

  const frozen = quarter.frozen;

  const apply = (forcedState: WindowOverride) => {
    void setWindow
      .mutateAsync({ forcedState, reason: reason || undefined })
      .then(() => {
        toast.success(
          forcedState === 'NONE'
            ? `${quarter.label} follows its window again`
            : `${quarter.label} ${forcedState === 'FORCED_OPEN' ? 'opened' : 'closed'}`,
        );
        setReason('');
      })
      .catch((e: unknown) => toast.error('Could not change the window', { description: err(e) }));
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            {quarter.label}
            {isCurrent && <span className="text-xs font-normal text-primary">current</span>}
          </CardTitle>
          <CardDescription>
            {formatPlainDate(quarter.startsOn)} – {formatPlainDate(quarter.endsOn)} · window{' '}
            {formatPlainDate(quarter.window.opensOn)} – {formatPlainDate(quarter.window.closesOn)}
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {frozen && (
            <Badge variant="soft-muted" className={PILL_CLASS}>
              Frozen
            </Badge>
          )}
          <Badge variant={windowReasonVariant(quarter.window.reason)} className={PILL_CLASS}>
            {WINDOW_REASON_LABELS[quarter.window.reason]}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {frozen && (
          <Alert>
            <Snowflake className="size-4" />
            <AlertDescription className="text-xs">
              Frozen by <span className="font-medium">{quarter.frozenByName ?? 'HR'}</span>
              {quarter.frozenAt ? ` on ${new Date(quarter.frozenAt).toLocaleDateString()}` : ''}. No
              assessment in this quarter accepts a change at any stage.
            </AlertDescription>
          </Alert>
        )}

        {quarter.forcedByName && (
          // Amber even when the override OPENS the window: that a human overrode
          // the calendar is something every later reviewer should notice.
          <Alert>
            <AlertDescription className="text-xs">
              {quarter.forcedState === 'FORCED_OPEN' ? 'Opened' : 'Closed'} manually by{' '}
              <span className="font-medium">{quarter.forcedByName}</span>
              {quarter.forcedAt ? ` on ${new Date(quarter.forcedAt).toLocaleDateString()}` : ''}
              {quarter.forcedReason ? ` — ${quarter.forcedReason}` : ''}
            </AlertDescription>
          </Alert>
        )}

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void setDates
              .mutateAsync({ opensOn, closesOn })
              .then(() => toast.success(`${quarter.label} window saved`))
              .catch((x: unknown) => {
                setOpensOn(quarter.window.opensOn);
                setClosesOn(quarter.window.closesOn);
                toast.error('Could not save the window', { description: err(x) });
              });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor={`opens-${quarter.id}`} className="text-xs">
              Opens
            </Label>
            <PlainDatePicker
              id={`opens-${quarter.id}`}
              value={opensOn}
              disabled={frozen}
              onChange={setOpensOn}
              className="h-9 w-[9.5rem]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`closes-${quarter.id}`} className="text-xs">
              Closes
            </Label>
            <PlainDatePicker
              id={`closes-${quarter.id}`}
              value={closesOn}
              disabled={frozen}
              onChange={setClosesOn}
              className="h-9 w-[9.5rem]"
            />
          </div>
          <Button hint={"Save the dates of this window"}
            type="submit"
            size="sm"
            variant="outline"
            disabled={
              frozen ||
              setDates.isPending ||
              (opensOn === quarter.window.opensOn && closesOn === quarter.window.closesOn)
            }
          >
            Save window
          </Button>
        </form>

        <ReviewChainRow quarter={quarter} />

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded against the override)"
            disabled={frozen}
            className="min-w-[200px] flex-1"
          />
          {/* Each of these lands the moment it is clicked and is undone only by a
              second deliberate act, so each asks first. The descriptions say what
              changes for the people in the quarter, not what the button is called. */}
          <ConfirmButton
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={frozen || setWindow.isPending || quarter.forcedState === 'FORCED_OPEN'}
            title={`Force ${quarter.label} open?`}
            description={`Everyone can submit their ${quarter.label} self assessment from now on, whatever the dates say, until this is changed back. It is recorded against your name.`}
            confirmLabel="Force open"
            pending={setWindow.isPending}
            onConfirm={() => apply('FORCED_OPEN')}
          >
            <LockOpen className="size-3.5" />
            Force open
          </ConfirmButton>
          <ConfirmButton
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={frozen || setWindow.isPending || quarter.forcedState === 'FORCED_CLOSED'}
            title={`Force ${quarter.label} closed?`}
            description={`Nobody can submit their ${quarter.label} self assessment from now on, even if the window is open on the calendar. Anyone part-way through one will be stopped.`}
            confirmLabel="Force closed"
            destructive
            pending={setWindow.isPending}
            onConfirm={() => apply('FORCED_CLOSED')}
          >
            <Lock className="size-3.5" />
            Force closed
          </ConfirmButton>
          <ConfirmButton
            size="sm"
            variant="ghost"
            className="gap-1.5"
            disabled={frozen || setWindow.isPending || quarter.forcedState === 'NONE'}
            title={`Drop the override on ${quarter.label}?`}
            description={`${quarter.label} goes back to opening and closing on its own dates. Whether anyone can submit right now depends on today's date rather than on your override.`}
            confirmLabel="Follow calendar"
            pending={setWindow.isPending}
            onConfirm={() => apply('NONE')}
          >
            <RotateCcw className="size-3.5" />
            Follow calendar
          </ConfirmButton>
          <ConfirmButton
            size="sm"
            variant={frozen ? 'outline' : 'secondary'}
            className="gap-1.5"
            disabled={setFrozen.isPending}
            title={frozen ? `Unfreeze ${quarter.label}?` : `Freeze ${quarter.label}?`}
            description={
              frozen
                ? `Every stage of ${quarter.label} reopens — self assessments, manager, HR and management reviews can all be submitted again.`
                : `${quarter.label} shuts to every stage. Nobody can submit, review, accept or change anything in it until it is unfrozen. This is the step that says the quarter is settled.`
            }
            confirmLabel={frozen ? 'Unfreeze' : 'Freeze quarter'}
            destructive={!frozen}
            pending={setFrozen.isPending}
            onConfirm={() => {
              void setFrozen
                .mutateAsync({ frozen: !frozen })
                .then(() =>
                  toast.success(`${quarter.label} ${frozen ? 'unfrozen' : 'frozen'}`, {
                    description: frozen
                      ? 'Reviews can be submitted again.'
                      : 'No further changes can be made to its assessments.',
                  }),
                )
                .catch((x: unknown) =>
                  toast.error('Could not change the freeze', { description: err(x) }),
                );
            }}
          >
            <Snowflake className="size-3.5" />
            {frozen ? 'Unfreeze' : 'Freeze quarter'}
          </ConfirmButton>
        </div>
      </CardContent>
    </Card>
  );
}

/** Create the next financial year's cycle and its four quarters. */
function CreateYear({ existing }: { existing: number[] }) {
  const create = useCycleMutation<{ fyStartYear: number }>((body) =>
    api.post<Cycle>('/cycles', body),
  );
  // The next FY that does not exist yet - the only one anyone is here to add.
  const suggested = React.useMemo(() => {
    let year = currentPeriod().fyStartYear;
    while (existing.includes(year)) year += 1;
    return year;
  }, [existing]);
  const [fyStartYear, setFyStartYear] = React.useState(String(suggested));

  React.useEffect(() => setFyStartYear(String(suggested)), [suggested]);

  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void create
          .mutateAsync({ fyStartYear: Number(fyStartYear) })
          .then((cycle) =>
            toast.success(`${cycle.label} ready`, { description: 'Four quarters created.' }),
          )
          .catch((x: unknown) => toast.error('Could not create the year', { description: err(x) }));
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="fy-start" className="text-xs">
          Financial year starting April
        </Label>
        <Input
          id="fy-start"
          type="number"
          min={2020}
          max={2100}
          value={fyStartYear}
          onChange={(e) => setFyStartYear(e.target.value)}
          className="h-9 w-28"
        />
      </div>
      <Button hint={"Create the review cycle"} type="submit" size="sm" disabled={create.isPending}>
        Create {fiscalYearLabel(Number(fyStartYear) || suggested)}
      </Button>
    </form>
  );
}

/**
 * Assessment years, their quarter windows, and the freeze.
 *
 * The override is what makes a missed quarter recoverable — without it a window
 * that has passed could never be reopened, and the cycle would stall permanently
 * for anyone who was on leave that week. The freeze is the other end of the same
 * quarter: once every review has landed, HR settles it and nothing writes again.
 */
export function CyclesPage() {
  const { data: cycles, isPending, error } = useCycles();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  if (isPending) return <PageContainer><Skeleton className="h-64 w-full" /></PageContainer>;
  if (error) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  // Newest first from the API; the current year is the one HR wants on arrival.
  const thisYear = currentPeriod().fyStartYear;
  const cycle = cycles.find((c) => c.id === selectedId) ??
    cycles.find((c) => c.fyStartYear === thisYear) ??
    cycles[0];

  return (
    <PageContainer>
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>{cycle?.label ?? 'No assessment year yet'}</CardTitle>
              <CardDescription>
                {cycle
                  ? `${formatPlainDate(cycle.startsOn)} – ${formatPlainDate(cycle.endsOn)} · April to March, four quarters. Self assessment opens for the last 7 days of each quarter unless you change it below.`
                  : 'Create the first financial year to set up its four quarters.'}
              </CardDescription>
            </div>
            {cycles.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {cycles.map((c) => (
                  <Button hint={"Show this cycle"}
                    key={c.id}
                    size="sm"
                    variant={c.id === cycle?.id ? 'secondary' : 'ghost'}
                    onClick={() => setSelectedId(c.id)}
                  >
                    {c.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <CreateYear existing={cycles.map((c) => c.fyStartYear)} />
        </CardHeader>
      </Card>

      <Alert>
        <AlertDescription className="text-sm">
          Forcing a window open lets employees submit outside the configured dates. It is recorded
          against the quarter and shown to the employee and every reviewer — use it to recover a
          missed quarter, not to extend one quietly. Freezing is the opposite and the last step:
          once every review has landed, it closes the quarter to all four stages.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {cycle?.quarters.map((q) => (
          <QuarterRow key={q.id} quarter={q} isCurrent={q.index === cycle.currentQuarterIndex} />
        ))}
      </div>

      {cycle && <RatingTargets cycleId={cycle.id} cycleLabel={cycle.label} />}
    </PageContainer>
  );
}

/**
 * The expected grade distribution for the year.
 *
 * Lives on this page rather than the calibration panel because it is a decision about
 * the whole year, taken once, while the panel is the quarterly work of applying it.
 * Putting it beside the sessions would invite editing the curve to make a particular
 * sitting come out right.
 *
 * Validated as a set — six whole numbers totalling exactly 100 — using the same
 * function the API rejects with, so the button and the server cannot disagree.
 */
function RatingTargets({ cycleId, cycleLabel }: { cycleId: string; cycleLabel: string }) {
  const { data: targets } = useRatingTargets(cycleId);
  const save = useSetRatingTargets(cycleId);
  const [draft, setDraft] = React.useState<Record<Grade, string> | null>(null);

  // Server is the source of truth; a rejected save must snap back rather than keep
  // showing numbers that were never stored.
  React.useEffect(() => {
    if (!targets) return;
    setDraft(
      Object.fromEntries(targets.map((t) => [t.grade, String(t.targetPct)])) as Record<
        Grade,
        string
      >,
    );
  }, [targets]);

  if (!draft) return null;

  const parsed: GradeTarget[] = GRADES.map((grade) => ({
    grade,
    targetPct: Number(draft[grade]),
  }));
  const validation = validateTargets(parsed);
  const dirty = targets?.some((t) => String(t.targetPct) !== draft[t.grade]) ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Expected rating distribution</CardTitle>
        <CardDescription>
          What share of {cycleLabel} should end each grade. A guideline the calibration
          panel measures against — nobody is ever moved automatically because of it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {GRADES.map((grade) => (
            <div key={grade} className="space-y-1">
              <Label htmlFor={`target-${grade}`} className="text-xs">
                {grade}
              </Label>
              <div className="flex items-center gap-1">
                <Input
                  id={`target-${grade}`}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={draft[grade]}
                  onChange={(e) => setDraft({ ...draft, [grade]: e.target.value })}
                  className="h-9 text-right"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p
            className={cn(
              'text-sm',
              validation.ok ? 'text-muted-foreground' : 'text-warning',
            )}
          >
            {validation.ok
              ? `Totals ${validation.totalPct}%.`
              : (validation.issues[0]?.message ?? 'These do not add up to 100%.')}
          </p>
          <Button hint={"Save the target rating distribution"}
            size="sm"
            disabled={!validation.ok || !dirty || save.isPending}
            onClick={() =>
              save.mutate(parsed, {
                onError: (e) => toast.error('Could not save', { description: e.message }),
                onSuccess: () => toast.success('Distribution saved'),
              })
            }
          >
            {save.isPending ? 'Saving…' : 'Save distribution'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
